import { ISelectedMember, QuoteType } from '@/types';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { generateQuote } from '@/queries/retail-health-insurance.queries';
import { getMemberAndDependentsDetails } from '@/queries/memberDetails.queries';
import {
  buildRhiGenerateQuoteKey,
  buildRhiQuotesKey,
  RHI_QUERY_KEYS,
  RHI_QUERY_NAMESPACE,
} from '@/constants/queryKeys';
import {
  DEFAULT_SELECTED_MEMBERS,
  DEFAULT_SUM_INSURED,
  useRhiFlowStore,
} from '@/store/rhiFlowStore';

// Define the shape of your context, now including relationType
interface HealthInsuranceContext {
  leadId: string;
  setLeadId: React.Dispatch<React.SetStateAction<string>>;
  benefitId: string;
  setBenefitId: React.Dispatch<React.SetStateAction<string>>;
  quotes: QuoteType[];
  setQuotes: (quotes: QuoteType[], riders?: Array<string>) => void;
  essentialRidersIncluded: boolean;
  setEssentialRidersIncluded: React.Dispatch<React.SetStateAction<boolean>>;
  selectedMembers: ISelectedMember[];
  proposalId: string;
  setProposalId: React.Dispatch<React.SetStateAction<string>>;
  includedRiders: Array<string>;
  setIncludedRiders: React.Dispatch<React.SetStateAction<Array<string>>>;
  setSelectedMembers: React.Dispatch<React.SetStateAction<ISelectedMember[]>>;
  isLoadingQuotes: boolean;
  isFetchingQuotes: boolean;
  quotesError: unknown;
  refetchQuotes: () => void;
  selectedSumInsured: number;
  setSelectedSumInsured: React.Dispatch<React.SetStateAction<number>>;
  selectedPlanId: string;
  setSelectedPlanId: React.Dispatch<React.SetStateAction<string>>;
  selectedQuote?: QuoteType;
  resetQuoteCache: () => void;
}

// Create the context with default values
const HealthInsuranceContext = createContext<
  HealthInsuranceContext | undefined
>(undefined);

const EMPTY_QUOTES: QuoteType[] = [];

const useStoreDispatch = <T,>(
  selector: (state: ReturnType<typeof useRhiFlowStore.getState>) => T,
  setter: (value: T) => void,
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const value = useRhiFlowStore(selector);
  const dispatch = useCallback<React.Dispatch<React.SetStateAction<T>>>(
    (next) => {
      const resolved =
        typeof next === 'function'
          ? (next as (prev: T) => T)(value)
          : next;
      setter(resolved);
    },
    [setter, value],
  );
  return [value, dispatch];
};

// Create a provider component
export const HealthInsuranceContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const quoteSnapshot = useRhiFlowStore((s) => s.quoteSnapshot);
  const proposalId = useRhiFlowStore((s) => s.proposalId);

  const setLeadIdStore = useRhiFlowStore((s) => s.setLeadId);
  const setBenefitIdStore = useRhiFlowStore((s) => s.setBenefitId);
  const setSelectedPlanIdStore = useRhiFlowStore((s) => s.setSelectedPlanId);
  const setSelectedMembersStore = useRhiFlowStore((s) => s.setSelectedMembers);
  const setSelectedSumInsuredStore = useRhiFlowStore((s) => s.setSelectedSumInsured);
  const setEssentialRidersIncludedStore = useRhiFlowStore(
    (s) => s.setEssentialRidersIncluded,
  );
  const setIncludedRidersStore = useRhiFlowStore((s) => s.setIncludedRiders);
  const setProposalIdStore = useRhiFlowStore((s) => s.setProposalId);
  const setQuoteSnapshot = useRhiFlowStore((s) => s.setQuoteSnapshot);

  const [leadIdValue, setLeadId] = useStoreDispatch(
    (s) => s.leadId,
    setLeadIdStore,
  );
  const [benefitIdValue, setBenefitId] = useStoreDispatch(
    (s) => s.benefitId,
    setBenefitIdStore,
  );
  const [selectedPlanIdValue, setSelectedPlanId] = useStoreDispatch(
    (s) => s.selectedPlanId,
    setSelectedPlanIdStore,
  );
  const [selectedMembersValue, setSelectedMembers] = useStoreDispatch(
    (s) => s.selectedMembers,
    setSelectedMembersStore,
  );
  const [selectedSumInsuredValue, setSelectedSumInsured] = useStoreDispatch(
    (s) => s.selectedSumInsured,
    setSelectedSumInsuredStore,
  );
  const [essentialRidersIncludedValue, setEssentialRidersIncluded] =
    useStoreDispatch((s) => s.essentialRidersIncluded, setEssentialRidersIncludedStore);
  const [includedRidersValue, setIncludedRiders] = useStoreDispatch(
    (s) => s.includedRiders,
    setIncludedRidersStore,
  );

  const queryClient = useQueryClient();
  const generateQuoteOptions = generateQuote(
    leadIdValue ?? '',
    selectedSumInsuredValue ?? DEFAULT_SUM_INSURED,
    essentialRidersIncludedValue ?? false,
    {
      retry: 2,
      retryDelay: 1000,
      throwOnError: false,
    },
    [],
  );

  const hasSelectedPlan = !!selectedPlanIdValue;
  const generateQuoteKey = buildRhiGenerateQuoteKey(
    leadIdValue ?? '',
    selectedSumInsuredValue ?? DEFAULT_SUM_INSURED,
    essentialRidersIncludedValue ?? false,
    [],
  );

  useEffect(() => {
    if (!quoteSnapshot.length || !leadIdValue) return;

    const existing = queryClient.getQueryData<QuoteType[]>(generateQuoteKey);
    if (!existing?.length) {
      queryClient.setQueryData(generateQuoteKey, quoteSnapshot);
    }
  }, [
    quoteSnapshot,
    leadIdValue,
    selectedSumInsuredValue,
    essentialRidersIncludedValue,
    queryClient,
    generateQuoteKey,
  ]);

  const {
    data: quotes = EMPTY_QUOTES,
    isLoading: isLoadingQuotes,
    isFetching: isFetchingQuotes,
    error: quotesError,
    refetch: refetchQuotes,
  } = useQuery<QuoteType[]>({
    ...generateQuoteOptions,
    queryKey: generateQuoteKey,
    staleTime: hasSelectedPlan ? Infinity : 5 * 60 * 1000,
    refetchOnMount: hasSelectedPlan ? false : true,
    refetchOnReconnect: hasSelectedPlan ? false : true,
  });

  useEffect(() => {
    if (quotes.length > 0) {
      setQuoteSnapshot(quotes);
    }
  }, [quotes, setQuoteSnapshot]);

  useEffect(() => {
    if (!benefitIdValue) return;

    queryClient.prefetchQuery(getMemberAndDependentsDetails(benefitIdValue));
  }, [benefitIdValue, queryClient]);

  const selectedQuote = useMemo(() => {
    if (!quotes || quotes.length === 0) {
      return undefined;
    }

    if (selectedPlanIdValue) {
      const matchingQuote = quotes.find(
        (quote) => quote.planId === selectedPlanIdValue,
      );
      if (matchingQuote) {
        return matchingQuote;
      }
    }

    return quotes[0];
  }, [quotes, selectedPlanIdValue]);

  const setQuotes = useCallback((nextQuotes: QuoteType[], riders?: Array<string>) => {
    const mainQueryKey = buildRhiGenerateQuoteKey(
      leadIdValue ?? '',
      selectedSumInsuredValue ?? DEFAULT_SUM_INSURED,
      essentialRidersIncludedValue ?? false,
      [],
    );

    queryClient.setQueryData(mainQueryKey, nextQuotes);
    if (nextQuotes.length > 0) {
      setQuoteSnapshot(nextQuotes);
    }

    const activeRiders = riders ?? includedRidersValue;
    if (activeRiders.length > 0 && selectedPlanIdValue) {
      queryClient.setQueryData(
        buildRhiQuotesKey(
          leadIdValue ?? '',
          selectedSumInsuredValue ?? DEFAULT_SUM_INSURED,
          essentialRidersIncludedValue ?? false,
          activeRiders,
          [selectedPlanIdValue],
        ),
        nextQuotes,
      );
    }
  }, [
    leadIdValue,
    selectedSumInsuredValue,
    essentialRidersIncludedValue,
    selectedPlanIdValue,
    queryClient,
    includedRidersValue,
    setQuoteSnapshot,
  ]);

  const resetQuoteCache = useCallback(() => {
    queryClient.removeQueries({
      queryKey: [RHI_QUERY_NAMESPACE, RHI_QUERY_KEYS.GENERATE_QUOTE],
    });
    queryClient.removeQueries({
      queryKey: [RHI_QUERY_NAMESPACE, RHI_QUERY_KEYS.QUOTES],
    });
  }, [queryClient]);

  const setProposalId: React.Dispatch<React.SetStateAction<string>> =
    useCallback(
      (value) => {
        const prev = useRhiFlowStore.getState().proposalId;
        const next = value instanceof Function ? value(prev ?? '') : value;
        setProposalIdStore(next);
      },
      [setProposalIdStore],
    );

  return (
    <HealthInsuranceContext.Provider
      value={{
        includedRiders: includedRidersValue,
        setIncludedRiders,
        selectedMembers: selectedMembersValue ?? DEFAULT_SELECTED_MEMBERS,
        setSelectedMembers,
        essentialRidersIncluded: essentialRidersIncludedValue ?? false,
        setEssentialRidersIncluded,
        benefitId: benefitIdValue ?? '',
        setBenefitId,
        leadId: leadIdValue ?? '',
        setLeadId,
        proposalId: proposalId ?? '',
        setProposalId,
        quotes,
        setQuotes,
        isLoadingQuotes,
        isFetchingQuotes,
        quotesError,
        refetchQuotes,
        selectedSumInsured: selectedSumInsuredValue ?? DEFAULT_SUM_INSURED,
        setSelectedSumInsured,
        selectedPlanId: selectedPlanIdValue ?? '',
        setSelectedPlanId,
        selectedQuote,
        resetQuoteCache,
      }}
    >
      {children}
    </HealthInsuranceContext.Provider>
  );
};

// Create a custom hook for easier access
export const useHealthInsuranceContext = () => {
  const context = useContext(HealthInsuranceContext);
  if (!context) {
    throw new Error(
      'useHealthInsuranceContext must be used within a HealthInsuranceContextProvider',
    );
  }
  return context;
};
